import React, { useCallback, useEffect, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { useAuth } from '../../contexts/authContext';
import { IServiceResult, useSteps } from '../../contexts/stepContext';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { getParamQuery } from '../../services/optIn';
import { Title } from '../../styles/global';
import { findingsCountOf, severityForCount } from '../../utils/services';
import Accordion from '../Accordion';
import { AlertBox } from '../AlertBox';
import Badge, { BadgeProps } from '../Badge';
import { Content } from '../BodyStep/styles';
import Button from '../Button';
import FlowButton from '../FlowButton';
import ResultDetails from '../ResultDetails';
import { KindData, kindOf, KnownKind } from '../ResultDetails/types';
import {
  BadgeResult,
  FindingsCount,
  FindingsList,
  FindingsOverview,
  MessageError,
  ProcessCard,
  ProcessField,
  ProcessList,
  ProfileContent,
  ProfileData,
  ResultContent,
  ResultStatus,
  ServiceChip,
  ServiceLabel,
  SummaryTags,
} from './styels';

const toneForResult = (mode?: string): BadgeProps['tone'] => {
  if (mode === 'success') return 'success';
  if (mode === 'warn') return 'warning';
  return 'danger';
};

const Result: React.FC = () => {
  const [optinText, setOptinText] = useState<string>('');

  const {
    selfie,
    name,
    documentNum,
    resetFlow,
    result,
    setResult,
    messageError,
    setMessageError,
    currentStep,
    flowSteps,
    handleJumpSteps,
    serviceError,
    setServiceError,
    serviceResults,
    setServiceResults,
    personType,
  } = useSteps();
  const { accessDash, signOut } = useAuth();
  const { translationPage } = useAppTranslation();
  const { tp } = translationPage(tr => tr.components.result);
  const { tp: tpDetails } = translationPage(tr => tr.components.resultDetails);

  // A empresa não cobra consultas que deram falha (mesmo quando a falha
  // trouxe apontamentos parciais antes de estourar o timeout) — mostrar esses
  // apontamentos desestimula o cliente a pedir a consulta de novo. Em vez
  // disso, qualquer falha manda direto para o preenchimento de dados, sem
  // reaproveitar os dados/serviço parcialmente apurados. Na Pessoa Jurídica a
  // etapa de dados é a do CNPJ.
  const handleRetryFromDataEntry = useCallback(() => {
    const identificationIndex = flowSteps.findIndex(
      step => step === 'identification' || step === 'company',
    );
    const targetStep = identificationIndex === -1 ? 1 : identificationIndex + 1;

    setResult('');
    setMessageError('');
    setServiceError('');
    setServiceResults([]);
    handleJumpSteps(targetStep - currentStep);
  }, [
    flowSteps,
    currentStep,
    handleJumpSteps,
    setResult,
    setMessageError,
    setServiceError,
    setServiceResults,
  ]);

  const returnToPlatform = useCallback(() => {
    signOut();
  }, [signOut]);

  // Apontamentos confirmados (Reprovado) continuam um accordion por serviço —
  // cada um é uma informação real e distinta. Falhas não: como toda falha já
  // mostra o mesmo texto genérico (não revelamos mais qual serviço falhou nem
  // por quê), não faz sentido repetir um accordion idêntico por serviço que
  // falhou — vira um único aviso, não importa se foi 1 ou 5 serviços.
  const errorResults = serviceResults.filter(item => item.result === 'error');
  const failureResults = serviceResults.filter(item => item.result === 'warn');
  // Sem apontamento, mas com dados para mostrar (Score sem restrição, Dados
  // Adicionais PJ, Validação PJ ativa). Não entram na contagem de
  // apontamentos — só ganham o próprio card, no estilo "Sem apontamentos".
  const successResults = serviceResults.filter(
    item => item.result === 'success',
  );
  const hasFailure = failureResults.length > 0;

  const kindTitles: Record<KnownKind, string> = {
    score: tpDetails('titles.score'),
    dados_pj: tpDetails('titles.dados_pj'),
    validacao_pj: tpDetails('titles.validacao_pj'),
  };

  // O backend já devolve o NOME do serviço — o código interno não existe mais
  // nesta resposta, nem na tela nem no tráfego HTTP. Quando ele vem nulo
  // (flag "mostrar qual serviço apontou" desligada), não inventamos nome: o
  // card dos serviços do protocolo novo leva o título genérico do `kind` (o
  // conteúdo já diz do que se trata), e os demais o título genérico de sempre.
  const labelForService = (item: IServiceResult): string => {
    if (item.service) return item.service;
    const kind = kindOf(item);
    return kind ? kindTitles[kind] : tp('title');
  };

  // Razão social da empresa consultada, quando algum serviço PJ a trouxe —
  // a consulta PJ não coleta nome, e o atendente precisa ver de quem é o CNPJ.
  const consultedCompanyName =
    personType === 'PJ'
      ? serviceResults
          .map(item =>
            kindOf(item) ? (item.additionalData?.[0] as KindData) : undefined,
          )
          .map(data =>
            data && data.kind !== 'score' ? data.razaoSocial : undefined,
          )
          .find(Boolean)
      : undefined;

  // Conteúdo do card de um serviço: pelo `kind` quando é do protocolo novo;
  // senão a lista de processos/registros de sempre.
  const renderDetails = (item: IServiceResult): React.ReactNode => {
    if (kindOf(item)) {
      return (
        <ResultDetails
          data={item.additionalData?.[0] as KindData}
          fallbackType={personType}
        />
      );
    }

    if (item.additionalData && item.additionalData.length > 0) {
      return (
        <ProcessList>
          {item.additionalData.map((proc, procIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <ProcessCard key={procIndex}>
              {(proc.subject || proc.branch) && (
                <strong>
                  {[proc.subject, proc.branch].filter(Boolean).join(' · ')}
                </strong>
              )}
              {proc.inferredSubjectDescription && (
                <p>{proc.inferredSubjectDescription}</p>
              )}
              {proc.type && (
                <ProcessField>
                  <dt>{tp('process.type')}</dt>
                  <dd>{proc.type}</dd>
                </ProcessField>
              )}
              {(proc.judgingBody || proc.court) && (
                <ProcessField>
                  <dt>{tp('process.judgingBody')}</dt>
                  <dd>
                    {[proc.court, proc.judgingBody].filter(Boolean).join(' | ')}
                  </dd>
                </ProcessField>
              )}
              {proc.defendands && proc.defendands.length > 0 && (
                <ProcessField>
                  <dt>{tp('process.defendands')}</dt>
                  <dd>
                    {proc.defendands
                      .map(d => [d.name, d.type].filter(Boolean).join(' | '))
                      .join('; ')}
                  </dd>
                </ProcessField>
              )}
              {proc.date && (
                <ProcessField>
                  <dt>{tp('process.date')}</dt>
                  <dd>{proc.date}</dd>
                </ProcessField>
              )}
              {proc.status && (
                <ProcessField>
                  <dt>{tp('process.status')}</dt>
                  <dd>{proc.status}</dd>
                </ProcessField>
              )}
              {proc.lawsuitNr && (
                <ProcessField>
                  <dt>{tp('process.lawsuitNr')}</dt>
                  <dd>{proc.lawsuitNr}</dd>
                </ProcessField>
              )}
              {typeof proc.value === 'number' && (
                <ProcessField>
                  <dt>{tp('process.value')}</dt>
                  <dd>
                    {proc.value.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })}
                  </dd>
                </ProcessField>
              )}
            </ProcessCard>
          ))}
        </ProcessList>
      );
    }

    // Reprovado sem detalhamento por processo (ex.: lista de restrição, que
    // só confirma que achou algo) — a mensagem do provedor aqui é só
    // "Reprovado", segura de mostrar. Num item 'success' sem `kind` conhecido
    // não há o que detalhar.
    return item.result === 'error' ? item.message : null;
  };

  // Serviço do resultado agregado (o primeiro apontamento). Some da tela
  // quando o backend não o identificou.
  const primaryServiceName = serviceError || undefined;

  // Total de apontamentos da pessoa consultada e em quais serviços eles
  // caíram — o cabeçalho de cada accordion já mostra o seu próprio serviço e a
  // sua própria contagem, mas o total exigia somar na cabeça.
  const totalFindings = errorResults.reduce(
    (sum, item) => sum + findingsCountOf(item),
    0,
  );

  // Só entram os serviços que o backend soube identificar. Com a flag de
  // "mostrar qual serviço apontou" desligada ele devolve service: null em
  // todos os resultados, e aí a faixa mostra só o total — inventar um nome
  // seria pior do que omiti-lo.
  const servicesWithFindings = Array.from(
    new Set(
      errorResults
        .map(item => item.service)
        .filter((label): label is string => Boolean(label)),
    ),
  );

  useEffect(() => {
    getParamQuery().then(response => setOptinText(response.param.par_valor));
  }, []);
  return (
    <Content>
      {primaryServiceName && <BadgeResult>{primaryServiceName}</BadgeResult>}

      <Title>{tp('title')}</Title>
      <ProfileContent>
        {/* A consulta PJ não tem foto. */}
        {!isMobileOnly && selfie && <img src={selfie} alt="" />}
        <ProfileData>
          {name && (
            <>
              <p>{tp('fullName')}</p>
              <span>{name}</span>{' '}
            </>
          )}
          {consultedCompanyName && (
            <>
              <p>{tpDetails('company.corporateName')}</p>
              <span>{consultedCompanyName}</span>
            </>
          )}
          <p>{tp('document')}</p>
          <span>{documentNum}</span>
          <ResultContent mode={result}>
            {result === 'success' && (
              <ResultStatus>
                <FaCheck size={24} color="#fff" />
                {tp('status.recommended')}
              </ResultStatus>
            )}
            {result === 'error' && (
              <ResultStatus>
                <FaTimes size={24} color="#fff" />
                {tp('status.notRecommended')}
              </ResultStatus>
            )}
            {result === 'warn' && (
              <ResultStatus>
                <FaTimes size={24} color="#fff" />
                {tp('status.failed')}
              </ResultStatus>
            )}
          </ResultContent>
        </ProfileData>
      </ProfileContent>
      {totalFindings > 0 && (
        <FindingsOverview>
          <FindingsCount severity={severityForCount(totalFindings)}>
            {totalFindings}
          </FindingsCount>
          <span>
            {servicesWithFindings.length > 0
              ? tp('findingsOverview.withServices')
              : tp('findingsOverview.total')}
          </span>
          {servicesWithFindings.map(label => (
            <ServiceChip key={label}>{label}</ServiceChip>
          ))}
        </FindingsOverview>
      )}

      {errorResults.length > 0 || successResults.length > 0 || hasFailure ? (
        <FindingsList>
          {errorResults.map((item, index) => (
            <Accordion
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              // Já aberto: o atendente precisa ver o apontamento sem ter que
              // clicar. A seta continua permitindo recolher.
              defaultOpen
              summary={
                <>
                  <ServiceLabel>{labelForService(item)}</ServiceLabel>
                  <SummaryTags>
                    {findingsCountOf(item) > 0 && (
                      <FindingsCount
                        severity={severityForCount(findingsCountOf(item))}
                      >
                        {findingsCountOf(item)}
                      </FindingsCount>
                    )}
                    <Badge tone={toneForResult(item.result)}>
                      {tp('status.notRecommended')}
                    </Badge>
                  </SummaryTags>
                </>
              }
            >
              {renderDetails(item)}
            </Accordion>
          ))}
          {successResults.map((item, index) => (
            <Accordion
              // eslint-disable-next-line react/no-array-index-key
              key={`success-${index}`}
              defaultOpen
              summary={
                <>
                  <ServiceLabel>{labelForService(item)}</ServiceLabel>
                  <SummaryTags>
                    <Badge tone="success">{tp('status.recommended')}</Badge>
                  </SummaryTags>
                </>
              }
            >
              {renderDetails(item)}
            </Accordion>
          ))}
          {/* Um card POR SERVIÇO que falhou, nomeando cada um.

              Antes isto era um card único e anônimo. O raciocínio estava
              errado: eu juntei "não mostrar a mensagem do provedor" (certo —
              ela pode vazar um apontamento real, tipo "CPF possui processo
              criminal") com "não mostrar qual serviço falhou" (errado — o
              nome do serviço não revela nada sobre a pessoa). O resultado era
              o atendente vendo um card com apontamento e outro com falha, sem
              saber qual era qual nem o que precisava refazer.

              A mensagem continua genérica; o que passou a aparecer é só a
              identificação do serviço. */}
          {failureResults.map((item, index) => (
            <Accordion
              // eslint-disable-next-line react/no-array-index-key
              key={`fail-${index}`}
              defaultOpen
              summary={
                <>
                  <ServiceLabel>{labelForService(item)}</ServiceLabel>
                  <SummaryTags>
                    <Badge tone="warning">{tp('status.failed')}</Badge>
                  </SummaryTags>
                </>
              }
            >
              {tp('failureMessage')}
            </Accordion>
          ))}
        </FindingsList>
      ) : (
        <MessageError>{result === 'warn' && messageError}</MessageError>
      )}
      <AlertBox message={optinText} removeAnimation />
      {accessDash ? (
        <Button
          title={tp('buttons.returnToPlatform')}
          onClick={returnToPlatform}
          style={{ marginTop: 20 }}
        />
      ) : result === 'warn' ? (
        <FlowButton
          text={tp('buttons.tryAgain')}
          onClick={handleRetryFromDataEntry}
          style={{ marginTop: 20 }}
        />
      ) : (
        <Button
          title={tp('buttons.backToStart')}
          onClick={resetFlow}
          style={{ marginTop: 20 }}
        />
      )}
    </Content>
  );
};

export default Result;
